import React from "react";
import { useAppSelector } from "store";

import {
    Box,
    Card,
    CardContent,
    Collapse,
    Divider,
    Grid,
    Icon,
    Typography,
    useTheme
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
//@ts-ignore
import StopRoundedIcon from "@material-ui/icons/StopRounded";
import FiberManualRecordRoundedIcon from "@material-ui/icons/FiberManualRecordRounded";
import PlayArrowRoundedIcon from '@material-ui/icons/PlayArrowRounded';

import OtherStoreIconSvg from "assets/images/other-store-icon.svg";
import useColourPalette from "components/visuals/useColourPalette";

import {
    ClassifiedCompetitor,
    CompetitorClassification
} from "modules/customer/tools/location/competitionNew/competitor";
import { selectShowAlternateCategoryCompetitors } from "modules/customer/tools/location/competitionNew/competitionSlice";
import { selectPlaceCategories, selectSizeBuckets } from "modules/customer/tools/location/locationSlice";
import { PinnedLocation } from "modules/customer/tools/location/pinnedLocation";
import mathUtils from "utils/mathUtils";
import numberFormatter from "utils/numberFormatter";

const StyledCard = withStyles(theme => ({
    root: {
        position: "absolute",
        zIndex: theme.zIndex.drawer,
        margin: theme.spacing(2),
        bottom: theme.spacing(2),
        width: theme.spacing(34),
        // @ts-ignore
        backgroundColor: theme.palette.background.insight
    }
}))(Card);

const StyledCardContent = withStyles(theme => ({
    root: {
        "&:last-child": {
            padding: theme.spacing(2)
        }
    }
}))(CardContent);

const StyledDivider = withStyles(theme => ({
    root: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        // @ts-ignore
        backgroundColor: theme.palette.quaternary.light
    }
}))(Divider);

interface LegendProps {
    competitor?: ClassifiedCompetitor,
    selectedStore?: PinnedLocation;
}

const Legend: React.FC<LegendProps> = (props) => {
    const { competitor, selectedStore } = props;
    const theme = useTheme();
    const colourPalette = useColourPalette();
    const placeCategories = useAppSelector(selectPlaceCategories);
    const showAlternateCategoryCompetitors = useAppSelector(selectShowAlternateCategoryCompetitors);
    const sizeBuckets = useAppSelector(selectSizeBuckets);
    const competitorCategoryName = placeCategories.find(pc => pc.id === competitor?.primaryCategoryId)?.name ?? "Unknown";
    const sizeLabel = sizeBuckets.find(sb => sb.id === competitor?.sizeBucketId)?.label ?? "Unknown";
    let competitorColour = colourPalette.categorical[0];
    switch (competitor?.classification) {
        case CompetitorClassification.Direct:
            competitorColour = colourPalette.categorical[2];
            break;
        case CompetitorClassification.Standard:
            competitorColour = colourPalette.categorical[0];
            break;
        case CompetitorClassification.Indirect:
            competitorColour = colourPalette.categorical[5];
            break;
        case CompetitorClassification.Alternate:
            competitorColour = colourPalette.categorical[1];
            break;
        default:
            competitorColour = colourPalette.categorical[0];
            break;
    }

    return (
        <StyledCard elevation={0}>
            <StyledCardContent>
                <Collapse in={competitor !== undefined}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" component="div">
                                {competitor?.storeName}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" component="div" style={{ color: competitorColour, fontWeight: "bold" }} align="right">
                                {competitor?.classification}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" component="div">
                                Store category
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" component="div" align="right">
                                {competitorCategoryName}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" component="div">
                                Alternate store categories
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2" component="div" align="right">
                                {competitor?.alternateCategoryNames}
                            </Typography>
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant="body2" component="div">
                                Distance from selected store
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="subtitle2" component="div" align="right">
                                {`${numberFormatter.toDecimalPlaces(
                                    mathUtils.haversineDistance(
                                        selectedStore?.latitude ?? 0,
                                        selectedStore?.longitude ?? 0,
                                        competitor?.latitude ?? 0,
                                        competitor?.longitude ?? 0
                                    ) ?? 0, 1)
                                    }km`
                                }
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="body2" component="div">
                                Store size
                            </Typography>
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant="subtitle2" component="div" align="right">
                                {sizeLabel}
                            </Typography>
                        </Grid>
                    </Grid>
                    <StyledDivider />
                </Collapse>
                <Box display="flex" style={{ paddingTop: theme.spacing(0.25) }}>
                    <Icon fontSize="small">
                        <img src={OtherStoreIconSvg} height={24} width={24} alt="portfolio-competition-nearby-competition-legend-other-store-icon" />
                    </Icon>
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.25), paddingLeft: theme.spacing(0.5) }} gutterBottom>
                        &nbsp;&nbsp;Selected location
                    </Typography>
                </Box>
                <Box display="flex">
                    <PlayArrowRoundedIcon fontSize="medium" style={{ color: colourPalette.categorical[2], transform: "rotate(90deg)" }} />
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.25) }} gutterBottom>
                        &nbsp;&nbsp;Direct competitor
                    </Typography>
                </Box>
                <Box display="flex">
                    <StopRoundedIcon fontSize="medium" style={{ color: colourPalette.categorical[0], transform: "rotate(45deg)" }} />
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.25) }} gutterBottom>
                        &nbsp;&nbsp;Competitor
                    </Typography>
                </Box>
                {showAlternateCategoryCompetitors && (
                    <Box display="flex">
                        <StopRoundedIcon fontSize="small" style={{ color: colourPalette.categorical[1], paddingLeft: theme.spacing(0.5) }} />
                        <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.25), paddingLeft: theme.spacing(0.5) }} gutterBottom>
                            &nbsp;&nbsp; Alternate category competitors
                        </Typography>
                    </Box>
                )}
                <Box display="flex">
                    <FiberManualRecordRoundedIcon fontSize="small" style={{ color: colourPalette.categorical[5], paddingLeft: theme.spacing(0.5) }} />
                    <Typography variant="body1" component="div" style={{ paddingTop: theme.spacing(0.25), paddingLeft: theme.spacing(0.5) }} gutterBottom>
                        &nbsp;&nbsp;Indirect competitor
                    </Typography>
                </Box>
            </StyledCardContent>
        </StyledCard>
    );
};

export default Legend;
